import imageCompression from 'browser-image-compression'
import { getIdToken } from 'firebase/auth'
import { auth } from '../utils/firebase/firebase'

const CLOUDFLARE_WORKER_URL = `https://image-api${
  process.env.GATSBY_USE_EMULATORS === 'true' ? '-dev' : ''
}.pickerwheel.workers.dev`

const getFileType = dataUrl => {
  const match = dataUrl.match(/data:image\/([^;]+);/)
  let fileType = match ? match[1] : 'unknown'

  if (fileType === 'svg+xml') {
    fileType = 'svg'
  }

  return fileType
}

const createExactSizeCanvas = (
  imageWidth,
  imageHeight,
  targetWidth,
  targetHeight,
  fileType,
) => {
  const isPortrait = imageHeight > imageWidth
  const effectiveTargetWidth = isPortrait ? targetHeight : targetWidth
  const effectiveTargetHeight = isPortrait ? targetWidth : targetHeight

  const xScale = effectiveTargetWidth / imageWidth
  const yScale = effectiveTargetHeight / imageHeight

  // Only scale down if image is larger than target, otherwise keep original size
  const shouldResize =
    fileType === 'svg'
      ? true
      : imageWidth > effectiveTargetWidth || imageHeight > effectiveTargetHeight
  const scale = shouldResize ? Math.min(xScale, yScale) : 1

  // Final dimensions (either original or scaled down)
  const finalWidth = imageWidth * scale
  const finalHeight = imageHeight * scale

  // Create canvas with final dimensions (not target dimensions!)
  const canvas = document.createElement('canvas')
  canvas.width = finalWidth
  canvas.height = finalHeight

  return canvas
}

const createProportionalCanvas = (imageWidth, imageHeight, maxSide) => {
  let xScale = maxSide / imageWidth
  let yScale = maxSide / imageHeight
  let scale = Math.max(xScale, yScale)
  const canvas = document.createElement('canvas')
  canvas.width = imageWidth * scale
  canvas.height = imageHeight * scale
  return canvas
}

const drawStretched = (canvas, image, filter) => {
  const context = canvas.getContext('2d')
  context.save()
  if (filter) context.filter = filter
  // Draw image with full bleed to reduce edge artifacts.
  context.drawImage(image, 0, 0, canvas.width, canvas.height)
  context.restore()
}

const getOptimizedDataUrl = (canvas, originalDataUrl) => {
  let newDataUrl
  if (topLeftIsTransparent(canvas.getContext('2d'))) {
    newDataUrl = canvas.toDataURL('image/png')
  } else {
    newDataUrl = canvas.toDataURL('image/jpeg', 0.5)
  }
  if (originalDataUrl && originalDataUrl.length < newDataUrl.length) {
    return originalDataUrl
  } else {
    return newDataUrl
  }
}

const topLeftIsTransparent = context => {
  let data = context.getImageData(0, 0, 1, 1).data
  return data[3] < 5
}

const hex = d => Number(d).toString(16).padStart(2, '0')

const meantToBeWhite = (r, g, b) => {
  // An edge effect and slight transparence probably made the
  // color slightly off-white.
  return r == g && r == b && r > 230
}

const getHexColor = (r, g, b, a) => {
  return `#${hex(r)}${hex(g)}${hex(b)}${hex(a)}`
}

const getTopLeftColor = image => {
  let context = document.createElement('canvas').getContext('2d')
  context.drawImage(image, 0, 0)
  let data = context.getImageData(0, 0, 1, 1).data
  if (meantToBeWhite(data[0], data[1], data[2])) {
    return '#FFFFFFFF'
  } else {
    return getHexColor(data[0], data[1], data[2], data[3])
  }
}

const isTransparent = rgbaColor => {
  let retVal = false
  let match = rgbaColor.match(/#\w\w\w\w\w\w(\w\w)/)
  if (match) {
    retVal = match[1] == '00'
  }
  return retVal
}

const boxFits = (a, r, b, w, h) => {
  //this symbol "a" represents half radian
  let d =
    Math.sqrt(Math.pow(r, 2) - Math.pow(h / 2, 2)) -
    Math.max((h * Math.cos(a)) / (2 * Math.sin(a)), b)
  return d >= w
}

const optimizeSliceImage = async (dataUrl, optimizedSize = 200) => {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => {
      let canvas
      const hdResolutions = {
        '720p': { width: 1280, height: 720 },
        // '1080p': { width: 1920, height: 1080 },
      }

      if (hdResolutions[optimizedSize]) {
        const fileType = getFileType(dataUrl)
        const { width, height } = hdResolutions[optimizedSize]
        canvas = createExactSizeCanvas(
          image.width,
          image.height,
          width,
          height,
          fileType,
        )
      } else {
        // Default to pixel-based size
        canvas = createProportionalCanvas(
          image.width,
          image.height,
          optimizedSize,
        )
      }

      drawStretched(canvas, image)
      resolve(getOptimizedDataUrl(canvas, dataUrl))
    }
    image.src = dataUrl
  })
}

const createTempImage = (width, height) => {
  const tempCanvas = document.createElement('canvas')
  tempCanvas.width = width
  tempCanvas.height = height
  return tempCanvas
}

export const isImageData = str => {
  // if (typeof str !== 'string') return false
  // return str.includes('data:image/') && str.includes(';base64')
  return str.includes('data:image/')
}

export const isImageBlob = str => {
  if (typeof str !== 'string') return false
  return str.includes('blob:http')
}

export const convertBase64ToBlob = str => {
  const base64ImageData = str
  const contentType = str.substring(str.indexOf(':') + 1, str.indexOf(';'))
  // const contentType = str.includes('image/jpeg') ? 'image/jpeg' : 'image/png'

  const byteCharacters = atob(
    base64ImageData.substr(`data:${contentType};base64,`.length),
  )
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
    const slice = byteCharacters.slice(offset, offset + 1024)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)

    byteArrays.push(byteArray)
  }
  const blob = new Blob(byteArrays, { type: contentType })

  const blobUrl = URL.createObjectURL(blob)

  return blobUrl
}

export const convertImageUrlToBase64 = async imageUrl => {
  const blob = await fetch(imageUrl).then(r => r.blob())
  const reader = new FileReader()
  reader.readAsDataURL(blob)
  return new Promise(resolve => {
    reader.onloadend = () => {
      resolve(reader.result)
    }
  })
}

export const convertUrlToBlobUrl = async url => {
  try {
    const response = await fetch(url)
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    return blobUrl
  } catch (error) {
    return null
  }
}

export const validateImageDimension = (src, maxSize) => {
  const image = new Image()

  const promise = new Promise((resolve, reject) => {
    image.onload = () => {
      const height = image.height
      const width = image.width
      if (width <= maxSize.width && height <= maxSize.height) {
        resolve({ width, height })
      } else {
        reject('Invalid image dimensions')
      }
    }

    // Reject promise on error
    image.onerror = () => {
      reject('Error occurred')
    }
  })

  // Setting the source makes it start downloading and eventually call `onload`
  image.src = src

  return promise
}

// export const extractImage = entry => {
//   let imageData = ''
//   if (entry) {
//     let match = entry.match(/<img>"(.*?)"/)
//     if (match) {
//       imageData = match[1]
//     }
//   }
//   return imageData
// }

const getSmartQualities = currentSizeKB => {
  if (currentSizeKB > 200) {
    return { initialQuality: 0.2, maxIteration: 10 }
  } else if (currentSizeKB > 175) {
    return { initialQuality: 0.25, maxIteration: 10 }
  } else if (currentSizeKB > 150) {
    return { initialQuality: 0.3, maxIteration: 10 }
  } else if (currentSizeKB > 125) {
    return { initialQuality: 0.35, maxIteration: 10 }
  } else if (currentSizeKB > 100) {
    return { initialQuality: 0.41, maxIteration: 10 }
  } else if (currentSizeKB >= 80) {
    return { initialQuality: 0.44, maxIteration: 10 }
  } else {
    return { initialQuality: 0.5, maxIteration: 1 }
  }
}

const compressImage = async file => {
  const currentSizeKB = file.size / 1024
  const { initialQuality, maxIteration } = getSmartQualities(currentSizeKB)

  const options = {
    maxSizeMB: 0.05, // 50KB
    useWebWorker: true,
    alwaysKeepResolution: true,
    maxIteration: maxIteration,
    initialQuality: initialQuality,
  }

  const compressedBlob = await imageCompression(file, options)
  const compressedFile = new File([compressedBlob], file.name, {
    type: compressedBlob.type,
    lastModified: Date.now(),
  })

  const blobUrl = URL.createObjectURL(compressedFile)
  return { compressedFile, blobUrl }
}

const dataUrlToFile = (dataUrl, filename) => {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

// Resize & Compress
const optimizeAndCompressImage = async (file, dataUrl) => {
  try {
    const resizedDataUrl = await optimizeSliceImage(dataUrl, '720p')

    const finalFormat = resizedDataUrl.startsWith('data:image/png')
      ? 'png'
      : 'jpg'
    const baseName = file.name.replace(/\.[^/.]+$/, '')
    const finalName = `${baseName}_${Date.now()}.${finalFormat}`
    const imageFile = dataUrlToFile(resizedDataUrl, finalName)

    if (imageFile.size / 1024 < 51) {
      const blobUrl = URL.createObjectURL(imageFile)
      // console.log((imageFile.size / 1024).toFixed(2), ' KB, ', blobUrl)
      return {
        imageUrl: blobUrl,
        compressedFile: imageFile,
      }
    }

    const { compressedFile, blobUrl } = await compressImage(imageFile)

    // console.log(
    //   (imageFile.size / 1024).toFixed(2),
    //   ' KB → ',
    //   (compressedFile.size / 1024).toFixed(2),
    //   ' KB, ',
    //   blobUrl,
    // )

    return {
      imageUrl: blobUrl,
      compressedFile,
      // fileSizeKB: (compressedFile.size / 1024).toFixed(2),
    }
  } catch (error) {
    console.error('Optimization failed:', error)
    throw error
  }
}

export const getR2ImageAsBase64 = async imageKey => {
  const response = await fetch(`${CLOUDFLARE_WORKER_URL}/get-image`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key: imageKey }),
  })

  if (!response.ok) {
    return null
    // throw new Error('Failed to fetch image from R2')
  }

  const blob = await response.blob()
  const reader = new FileReader()
  reader.readAsDataURL(blob)

  return new Promise(resolve => {
    reader.onloadend = () => {
      resolve(reader.result)
    }
  })
}

export const getR2StorageData = async (page, key) => {
  const user = auth.currentUser
  const idToken = await user.getIdToken()

  const response = await fetch(`${CLOUDFLARE_WORKER_URL}/get-list-data`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ page, key }),
  })

  if (!response.ok) {
    throw new Error('Failed to fetch data from R2')
  }

  const result = await response.json()

  if (!result.success) {
    throw new Error(result.message)
  }

  return result.data
}

export const saveListDataToR2 = async (page, key, data) => {
  const stringifiedData = JSON.stringify(data)

  const user = auth.currentUser
  const idToken = await user.getIdToken()

  const response = await fetch(`${CLOUDFLARE_WORKER_URL}/save-list-data`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      page,
      key,
      data: stringifiedData,
    }),
  })

  const result = await response.json()

  if (!result.success) {
    throw new Error('Failed to save list data')
  }

  return result
}

export const deleteR2StorageData = async (page, key) => {
  const user = auth.currentUser
  const idToken = await user.getIdToken()

  const response = await fetch(`${CLOUDFLARE_WORKER_URL}/delete-list-data`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ page, key }),
  })

  const result = await response.json()

  if (!result.success) {
    throw new Error('Failed to delete data from R2')
  }

  return result
}

export const prepareImageFormData = async (imageItems, page, key) => {
  const user = auth.currentUser
  const formData = new FormData()

  // Fetch all blobs simultaneously
  const fetchPromises = imageItems.map(async item => {
    // convert objectURL → Blob
    let imageUrl = item.imageUrl
    if (!imageUrl.startsWith('blob:')) {
      imageUrl = await getR2ImageAsBase64(`users/${user.uid}/${imageUrl}`)
    }
    const res = await fetch(imageUrl)
    const blob = await res.blob()
    return { blob, filename: item.filename, originalUrl: item.imageUrl }
  })

  const blobs = await Promise.all(fetchPromises)

  // Append all blobs to formData
  for (let i = 0; i < blobs.length; i++) {
    formData.append('files', blobs[i].blob, blobs[i].filename)
  }
  formData.append('page', page)
  formData.append('key', key)

  return formData
}

export const uploadImagesToR2 = async formData => {
  const user = auth.currentUser
  const idToken = await user.getIdToken()

  const response = await fetch(`${CLOUDFLARE_WORKER_URL}/upload-batch`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
    body: formData,
  })

  const data = await response.json()

  if (!data.success) {
    throw new Error('Batch upload failed')
  }

  // Return array of results
  return data.results.map(result => ({
    key: result.key,
  }))
}

export const deleteImagesFromR2 = async (files, page, key) => {
  const user = auth.currentUser
  const idToken = await user.getIdToken()

  const res = await fetch(`${CLOUDFLARE_WORKER_URL}/delete-batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ files, page, key }),
  })
  const data = await res.json()
  return data
}

export const deleteAllUserFoldersFromR2 = async () => {
  const user = auth.currentUser
  const idToken = await user.getIdToken()

  const res = await fetch(`${CLOUDFLARE_WORKER_URL}/delete-all-user-folders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  })
  return await res.json()
}

export const deleteFolderFromR2 = async (page, key) => {
  const user = auth.currentUser
  const idToken = await user.getIdToken()

  const res = await fetch(`${CLOUDFLARE_WORKER_URL}/delete-folder`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ page, key }),
  })
  return await res.json()
}

export const cleanupFolderFromR2 = async (page, key, keepFilenames) => {
  const user = auth.currentUser
  const idToken = await user.getIdToken()

  const res = await fetch(
    `${CLOUDFLARE_WORKER_URL}/cleanup-folder-by-filenames`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        page,
        key,
        keepFilenames,
      }),
    },
  )
  const data = await res.json()
  return data
}

export default {
  optimizeSliceImage,
  getTopLeftColor,
  isTransparent,
  boxFits,
  createTempImage,
  optimizeAndCompressImage,
}
